import type { AdminDashboardData } from '@workspace/api-backend'
import { MetricCard } from '@workspace/ui/components/metric-card'
import { TimeSeriesChart } from '@workspace/ui/components/time-series-chart'
import { BreakdownList } from './breakdown-list'
import { DashboardCard, DashboardSection } from './dashboard-card'
import { formatCents } from './dashboard-format'

const USAGE_LABELS: Record<string, string> = {
  PITCH_SESSION: 'AI pitch sessions',
  EVALUATION: 'Fund evaluations',
}

const ACCRUAL_LABELS: Record<string, string> = {
  VIEW: 'Data room views',
  RETURN_VISIT: 'Return visits',
  CALL_BOOKED: 'Calls booked',
}

export function RevenueSection({ data }: { data: AdminDashboardData }) {
  const { revenue } = data
  const totalAccrued = revenue.accrualsByType.reduce((sum, a) => sum + a.amountCents, 0)
  const usageRevenueCents = revenue.usagePurchasesByType.reduce((sum, p) => sum + p.revenueCents, 0)
  return (
    <DashboardSection title="Revenue">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Pay-as-you-go sales"
          value={formatCents(usageRevenueCents)}
          variant="highlight"
        />
        <MetricCard label="Outreach accrued" value={formatCents(totalAccrued)} />
        <MetricCard label="Outreach top-ups" value={formatCents(revenue.outreachTopUpCents)} />
        <MetricCard
          label="Entitlements"
          value={revenue.entitlements.active.toLocaleString()}
          subtitle={`${formatCents(revenue.entitlements.revenueCents)} purchased`}
        />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <DashboardCard title="Outreach revenue per day" subtitle="Accrued view/visit/call charges">
          <TimeSeriesChart
            data={revenue.byDay}
            series={[{ key: 'accruedCents', label: 'Accrued', color: 'var(--chart-1)' }]}
            valueFormatter={formatCents}
          />
        </DashboardCard>
        <div className="space-y-4">
          <DashboardCard title="Units sold" subtitle="Paid sessions and evaluations">
            <BreakdownList
              items={revenue.usagePurchasesByType.map((p) => ({
                label: USAGE_LABELS[p.type] ?? p.type,
                count: p.count,
                detail: formatCents(p.revenueCents),
              }))}
            />
          </DashboardCard>
          <DashboardCard title="Outreach accruals by type">
            <BreakdownList
              items={revenue.accrualsByType.map((a) => ({
                label: ACCRUAL_LABELS[a.type] ?? a.type,
                count: a.count,
                detail: formatCents(a.amountCents),
              }))}
            />
          </DashboardCard>
        </div>
      </div>
    </DashboardSection>
  )
}
